import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import * as THREE from 'three';
import { useCallback } from 'react';

// GLB Creator Class for creating new GLB files from grouped meshes
export class GLBCreator {
  constructor() {
    this.exporter = new GLTFExporter();
  }

  // Create new GLB from selected/grouped meshes
  createNewGLBFromMeshes = async (meshes, groupName, options = {}) => {
    try {
      const {
        filename = groupName || 'new_grouped_model',
        autoDownload = false,
        binary = true,
        centerGroup = false,
      } = options;

      if (!meshes || meshes.length === 0) {
        console.warn('No meshes provided to create GLB');
        return null;
      }

      // Create a new group for the meshes
      const newGroup = new THREE.Group();
      newGroup.name = filename;

      // Add each mesh to the new group
      meshes.forEach((mesh, index) => {
        let meshToAdd;

        // Handle different mesh input types (avoid cloning objects with THREE.Audio – "Audio source type cannot be copied")
        try {
          if (mesh.object && mesh.object.isMesh) {
            meshToAdd = mesh.object.clone();
          } else if (mesh.isMesh) {
            meshToAdd = mesh.clone();
          } else {
            console.warn('Invalid mesh object at index:', index);
            return;
          }
        } catch (e) {
          console.warn(
            'Skipping mesh at index (clone failed, e.g. contains audio):',
            index
          );
          return;
        }
        if (!meshToAdd) return;

        // Preserve the mesh's current world transform
        meshToAdd.updateMatrixWorld(true);

        // Ensure the mesh has a unique name
        meshToAdd.name = mesh.name || `mesh_${index}`;

        newGroup.add(meshToAdd);
      });
      // Export the new group as GLB
      const result = await this.exportToGLB(newGroup, { binary });

      if (autoDownload) {
        this.downloadFile(result.blob, `${filename}.glb`);
      }

      return {
        blob: result.blob,
        filename: `${filename}.glb`,
        meshCount: newGroup.children.length,
        group: newGroup,
      };
    } catch (error) {
      console.error(`Failed to create new GLB: ${groupName}`, error);
      return null;
    }
  };

  // Export to GLB using Promise
  exportToGLB = (object, options = { binary: true }) => {
    return new Promise((resolve, reject) => {
      this.exporter.parse(
        object,
        (gltf) => {
          const blob = new Blob([gltf], {
            type: options.binary ? 'model/gltf-binary' : 'application/json',
          });
          resolve({ blob, gltf });
        },
        (error) => {
          reject(error);
        },
        options
      );
    });
  };

  // Download file helper
  downloadFile = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };
}
const isGLBFile = (item) => {
  return item.name && item.name.toLowerCase().endsWith('.glb');
};

export const calculateTotalItems = ({
  camera_properties = [],
  light_data = [],
  assets_data = {},
  particles = [],
  expandedGLBs = new Set(),
  glbMeshes = {},
}) => {
  try {
    let total = 1; // Main camera

    total += camera_properties.length;
    total += light_data.length;

    ['image', 'mesh', 'video', 'audio', 'text'].forEach((arrKey) => {
      const items = assets_data[arrKey] || [];
      total += items.length;

      items.forEach((item) => {
        if (arrKey === 'mesh' && isGLBFile(item)) {
          const glbId = item._id || item.name;
          if (expandedGLBs.has(glbId) && glbMeshes[glbId]) {
            total += countGLBHierarchyNodes(glbMeshes[glbId]);
          }
        }
      });
    });

    total += particles.length;
    return total;
  } catch (error) {
    console.warn('Error calculating total items:', error);
    return 1; // Fallback
  }
};

/**
 * Get possible scene identifiers for a GLB asset (scene objects may be named by _id, name, file_name, or path).
 */
function getGlbLookupIds(glbAsset) {
  const ids = [];
  if (glbAsset._id) ids.push(glbAsset._id);
  if (glbAsset.name && !ids.includes(glbAsset.name)) ids.push(glbAsset.name);
  if (glbAsset.file_name && !ids.includes(glbAsset.file_name))
    ids.push(glbAsset.file_name);
  if (glbAsset.url && !ids.includes(glbAsset.url)) ids.push(glbAsset.url);
  if (glbAsset.temp_file_name && !ids.includes(glbAsset.temp_file_name))
    ids.push(glbAsset.temp_file_name);
  // Filename from asset_file_path (e.g. "https://cdn.../model.glb" -> "model.glb" or "model")
  const path = glbAsset.asset_file_path || glbAsset.asset_file_name || '';
  if (path && typeof path === 'string') {
    const segment = path.split(/[/\\]/).pop() || '';
    if (segment && !ids.includes(segment)) ids.push(segment);
    const withoutExt = segment.replace(/\.(glb|gltf)$/i, '');
    if (withoutExt && !ids.includes(withoutExt)) ids.push(withoutExt);
  }
  return ids;
}

export const extractMeshesFromGLB = (glbAsset, scene) => {
  // Validate inputs
  if (!glbAsset) {
    console.warn('Invalid glbAsset: missing asset');
    return [];
  }
  const lookupIds = getGlbLookupIds(glbAsset);
  if (lookupIds.length === 0) {
    console.warn(
      'Invalid glbAsset: no _id, name, file_name, url, or asset_file_path to look up'
    );
    return [];
  }
  if (!scene || typeof scene.getObjectByName !== 'function') {
    console.warn('Invalid scene: must be a valid Three.js Scene');
    return [];
  }

  let glbObject = null;

  // 🔍 1. Try direct lookup first
  for (const glbId of lookupIds) {
    glbObject = scene.getObjectByName(glbId);
    if (glbObject) break;
  }

  // 🔍 2. Try deep search if direct lookup failed
  if (!glbObject) {
    const lowerLookupIds = lookupIds.map(id => id.toString().toLowerCase());
    
    scene.traverse((child) => {
      if (glbObject) return;
      
      const identifiers = [
        child._id,
        child.userData?._id,
        child.userData?.id,
        child.name,
        child.file_name,
        child.uuid
      ].filter(Boolean);
      
      for (const id of identifiers) {
        const idStr = id.toString();
        const idLower = idStr.toLowerCase();
        
        // Exact match or case-insensitive match
        if (lookupIds.includes(idStr) || lowerLookupIds.includes(idLower)) {
          glbObject = child;
          break;
        }
        
        // "Contains" match for temp IDs or filenames
        if (lookupIds.some(lookupId => 
          lookupId.length > 10 && (idStr.includes(lookupId) || lookupId.includes(idStr))
        )) {
          glbObject = child;
          break;
        }
      }
    });
  }

  // 🔍 3. Final attempt: recursive name search for partial matches (e.g. "phoenix" in "phoenix_bird.glb")
  if (!glbObject) {
    const searchTerms = lookupIds.filter(id => id.length > 3).map(id => id.toLowerCase());
    
    scene.traverse((child) => {
      if (glbObject) return;
      if (!child.name) return;
      
      const childNameLower = child.name.toLowerCase();
      if (searchTerms.some(term => childNameLower.includes(term) || term.includes(childNameLower))) {
        glbObject = child;
      }
    });
  }


  const isShape =
    glbAsset.glb_type === 'shapes' ||
    glbObject?.glb_type === 'shapes' ||
    glbObject?.userData?.glb_type === 'shapes' ||
    glbObject?.isShapes;

  if (isShape) {
    return [];
  }

  if (glbObject) {
    return extractGLBHierarchyFromObject(glbObject, glbAsset);
  }

  console.warn(
    'GLB object not found in scene hierarchy:',
    lookupIds.join(', ')
  );
  return [];
};

function shouldSkipHierarchyNode(obj) {
  if (!obj) return true;
  if (obj.userData?.isHotspot || obj.deleteType === 'hotspot') return true;
  return false;
}

/** GLTFExporter / re-publish often adds Scene, AuxScene_1, etc. — not real content. */
function isGlbExporterGroupObject(obj) {
  if (!obj || obj.isMesh || obj.isSkinnedMesh || obj.isBone) return false;
  const name = String(obj.name || '').trim();
  const lower = name.toLowerCase();
  // Handles AuxScene, AuxScene_1, AuxScene_1_1, AuxScene-2, etc.
  if (/^auxscene(?:[_-]?\d+)*$/i.test(name)) return true;
  if (lower === 'scene' || lower === 'root scene' || lower === 'gltf scene') {
    return true;
  }
  if (/^scene(?:_\d+)*$/i.test(lower)) return true;
  if (lower === 'root' || lower === 'rootnode') return true;
  return false;
}

function unwrapExporterObject3d(obj) {
  let current = obj;
  while (
    current &&
    isGlbExporterGroupObject(current) &&
    current.children?.length === 1
  ) {
    current = current.children[0];
  }
  return current;
}

/** Collect real roots under a GLB (skip stacked exporter wrapper groups). */
function collectLogicalGlbRoots(object3d, hostGlbPivot = null) {
  if (!object3d) return [];
  const unwrapped = unwrapExporterObject3d(object3d) || object3d;
  const kids = (unwrapped.children || []).filter(
    (child) =>
      !shouldSkipHierarchyNode(child) &&
      !isNestedGlbAssetPivot(child, hostGlbPivot)
  );

  if (kids.length === 0) {
    return [unwrapped];
  }

  if (kids.length === 1 && isGlbExporterGroupObject(kids[0])) {
    return collectLogicalGlbRoots(kids[0], hostGlbPivot);
  }

  if (isGlbExporterGroupObject(unwrapped)) {
    return kids.flatMap((child) => collectLogicalGlbRoots(child, hostGlbPivot));
  }

  return [unwrapped];
}

/** Editor-created shape asset (Sphere, Torus, etc.) — not an inner GLB node. */
export function isEditorShapeAsset(obj) {
  if (!obj) return false;
  return !!(
    obj.isShapes ||
    obj.glb_type === 'shapes' ||
    obj.userData?.glb_type === 'shapes' ||
    obj.mesh_code ||
    obj.userData?.mesh_code
  );
}

/** Top-level scene mesh/shape asset (separate publish entry). */
export function isSceneMeshAsset(obj) {
  if (!obj) return false;
  return (
    obj.deleteType === 'mesh' &&
    obj.objType === '3d' &&
    !!(obj._id || obj.url || obj.file_name || obj.mesh_code)
  );
}

/** Editor pivot wraps gltf.scene — start hierarchy inside the loaded model. */
export function getGlbContentRoot(glbObject) {
  if (!glbObject) return null;

  // Bare shape Mesh: geometry is on this object; do not treat a parented shape child as content root.
  if (isEditorShapeAsset(glbObject) && glbObject.isMesh) {
    return unwrapExporterObject3d(glbObject) || glbObject;
  }

  let root = glbObject;
  if (
    glbObject.children?.length === 1 &&
    (glbObject.objType === '3d' || glbObject.deleteType === 'mesh')
  ) {
    const onlyChild = glbObject.children[0];
    if (!isNestedGlbAssetPivot(onlyChild, glbObject)) {
      root = onlyChild;
    }
  }
  return unwrapExporterObject3d(root) || root;
}

/**
 * Another uploaded GLB/shape pivot parented under this asset (not in-file GLB geometry).
 * Those appear once as asset children in the left panel — not inside internal GLB tree.
 */
export function isNestedGlbAssetPivot(child, hostGlbPivot) {
  if (!child || !hostGlbPivot || child === hostGlbPivot) return false;

  const isUserAsset =
    child.deleteType === 'mesh' ||
    child.deleteType === 'particle' ||
    child.deleteType === 'Light' ||
    child.deleteType === 'camera' ||
    child.deleteType === 'text' ||
    child.deleteType === 'hotspot' ||
    child.deleteType === 'empty' ||
    child.deleteType === 'image' ||
    child.deleteType === 'video' ||
    child.deleteType === 'audio' ||
    child.objType === '3d' ||
    child.objType === 'clouds' ||
    child.objType === 'foliage' ||
    child.objType === 'terrain' ||
    child.objType === 'water' ||
    child.objType === 'stars' ||
    child.objType === 'snow' ||
    child.objType === 'rain' ||
    child.objType === 'empty' ||
    child.isLight === true ||
    child.type?.includes('Light') ||
    child.type?.includes('Camera') ||
    !!child.userData?.text_material;

  if (!isUserAsset) return false;

  // Parent is an editor shape — any child asset is a separate asset, not part of this shape's GLB.
  if (isEditorShapeAsset(hostGlbPivot)) {
    return true;
  }

  const contentRoot = getGlbContentRoot(hostGlbPivot);
  if (child === contentRoot) return false;

  return true;
}

function buildHierarchyNode(object3d, glbAsset, hostGlbPivot) {
  const childNodes = [];
  (object3d.children || []).forEach((child) => {
    if (shouldSkipHierarchyNode(child)) return;
    if (isNestedGlbAssetPivot(child, hostGlbPivot)) return;
    childNodes.push(buildHierarchyNode(child, glbAsset, hostGlbPivot));
  });

  if (object3d.isMesh || object3d.isSkinnedMesh) {
    object3d.deleteType = 'mesh-extracted';
  }

  const nodeType =
    object3d.isMesh || object3d.isSkinnedMesh
      ? 'mesh'
      : object3d.isBone
        ? 'bone'
        : 'group';

  return {
    id: object3d.uuid,
    name:
      object3d.name ||
      object3d.userData?.file_name ||
      object3d.type ||
      'Unnamed',
    object: object3d,
    nodeType,
    parentGLB: glbAsset,
    children: childNodes,
    hierarchyNode: true,
  };
}

function isGlbExporterHierarchyWrapper(node) {
  if (!node || node.nodeType !== 'group') return false;
  return isGlbExporterGroupObject(node.object || { name: node.name });
}

/**
 * Republished GLBs stack Scene / AuxScene_* wrappers — collapse them for a stable tree.
 */
function normalizeHierarchyNode(node) {
  if (!node) return null;

  const normalizedChildren = (node.children || [])
    .map(normalizeHierarchyNode)
    .filter(Boolean);

  let result = {
    ...node,
    children: normalizedChildren,
  };

  while (
    isGlbExporterHierarchyWrapper(result) &&
    result.children.length === 1
  ) {
    result = result.children[0];
  }

  return result;
}

function normalizeHierarchyRoots(nodes) {
  return (nodes || []).map(normalizeHierarchyNode).filter(Boolean);
}

/** Build parent/child tree matching the Three.js object under the GLB pivot. */
export function extractGLBHierarchyFromObject(glbObject, glbAsset) {
  const contentRoot = getGlbContentRoot(glbObject);
  if (!contentRoot) return [];

  const logicalRoots = collectLogicalGlbRoots(contentRoot, glbObject);
  const roots = logicalRoots.map((obj) =>
    buildHierarchyNode(obj, glbAsset, glbObject)
  );

  return normalizeHierarchyRoots(roots);
}

export function countGLBHierarchyNodes(nodes) {
  if (!Array.isArray(nodes) || nodes.length === 0) return 0;
  return nodes.reduce(
    (sum, node) => sum + 1 + countGLBHierarchyNodes(node.children),
    0
  );
}

/** GLB inner hierarchy node (shared names across duplicate GLBs — match uuid only). */
export function isGlbHierarchyNodeItem(item) {
  return !!(item?.object && item?.parentGLB);
}

export function getGlbHierarchyNodeUuid(item) {
  if (!item?.object && !item?.id) return null;
  return item.object?.uuid || item.id || null;
}

export function glbHierarchyNodeMatchesUuid(item, uuid) {
  if (!uuid || !isGlbHierarchyNodeItem(item)) return false;
  const nodeUuid = getGlbHierarchyNodeUuid(item);
  return !!(nodeUuid && nodeUuid === uuid);
}

export function glbHierarchyContainsObject(nodes, { uuid, name } = {}) {
  return !!collectGlbHierarchyExpandKeys(nodes, { uuid, name });
}

/** Scope nested GLB hierarchy expand keys to a specific GLB instance. */
export function getGlbHierarchyExpandKey(glbRootId, nodeKey) {
  if (!nodeKey) return glbRootId || '';
  if (!glbRootId || nodeKey === glbRootId) return nodeKey;
  return `${glbRootId}::${nodeKey}`;
}

/** Whether a GLB pivot's subtree contains an object with the given uuid. */
export function glbPivotContainsObjectUuid(scene, glbRootId, objectUuid) {
  if (!scene || !glbRootId || !objectUuid) return false;
  const pivot = scene.getObjectByName(glbRootId);
  if (!pivot) return false;
  let found = false;
  pivot.traverse((obj) => {
    if (obj.uuid === objectUuid) found = true;
  });
  return found;
}

export function isGlbPivotObject(object, scene = null) {
  if (!object || object.deleteType !== 'mesh') return false;
  if (object.objType === '3d') return true;
  if (
    scene &&
    object.parent === scene &&
    (object.isGroup || object.type === 'Group')
  ) {
    return true;
  }
  return false;
}

/** Walk up from a mesh to the editor GLB pivot group (deleteType mesh, objType 3d). */
export function resolveGlbPivotAncestor(scene, object) {
  if (!object || !scene) return null;
  if (isGlbPivotObject(object, scene)) return object;

  let node = object;
  while (node && node !== scene) {
    if (isGlbPivotObject(node, scene)) return node;
    node = node.parent;
  }
  return null;
}

const EDITOR_ASSET_DELETE_TYPES = new Set([
  'image',
  'video',
  'audio',
  'text',
  'hotspot',
  'empty',
  'particle',
  'Light',
  'camera',
]);

/**
 * Real editor/published asset (image, text, nested GLB, …), not in-file GLB geometry.
 * These stay independently selectable even when parented under a GLB.
 */
export function isEditorRegisteredAsset(obj) {
  if (!obj || obj.deleteType === 'mesh-extracted') return false;

  const dt = obj.deleteType || obj.userData?.deleteType;
  if (EDITOR_ASSET_DELETE_TYPES.has(dt)) return true;
  if (obj.userData?.text_material) return true;
  if (obj.isLight || obj.isCamera) return true;

  if (
    dt === 'mesh' &&
    (obj._id || obj.url || obj.userData?._id) &&
    (obj.objType === '3d' || obj.userData?.objType === '3d')
  ) {
    return true;
  }

  return false;
}

function findEditorAssetBetween(object, scene, stopAt) {
  let node = object;
  while (node && node !== scene && node !== stopAt) {
    if (isEditorRegisteredAsset(node)) return node;
    node = node.parent;
  }
  return null;
}

function isDescendantOf(object, ancestor) {
  if (!object || !ancestor) return false;
  let current = object;
  while (current) {
    if (current === ancestor) return true;
    current = current.parent;
  }
  return false;
}

/**
 * When child-selection mode is off, never keep a pick on an inner GLB mesh.
 * When on, only allow children under the active GLB parent.
 * User-parented assets (image/video/text/…) under a GLB are not inner meshes.
 */
export function isGlbInnerMesh(scene, object) {
  if (!scene || !object) return false;
  if (isEditorRegisteredAsset(object)) return false;
  if (object.deleteType === 'mesh-extracted') return true;
  const pivot = resolveGlbPivotAncestor(scene, object);
  if (!pivot || pivot === object) return false;
  const nestedAsset = findEditorAssetBetween(object, scene, pivot);
  if (nestedAsset && nestedAsset !== pivot) return false;
  return true;
}

export function clampSelectionForChildMode(
  scene,
  object,
  activeParentForChildSelection = null
) {
  if (!object || !scene) return object;

  const pivot = resolveGlbPivotAncestor(scene, object);

  // Image/text/video/etc. (or a pick on their inner helper) under a GLB.
  const nestedAsset = findEditorAssetBetween(object, scene, pivot);
  if (nestedAsset && nestedAsset !== pivot) {
    return nestedAsset;
  }

  if (!pivot || pivot === object) return object;

  if (
    activeParentForChildSelection === pivot &&
    isDescendantOf(object, pivot)
  ) {
    return object;
  }

  return pivot;
}

/**
 * Expand keys (glb root id + nested node ids) to reveal an object in the left panel.
 */
export function collectGlbHierarchyExpandKeys(
  nodes,
  { uuid, name, glbRootId } = {}
) {
  if (!Array.isArray(nodes) || nodes.length === 0) return null;

  for (const node of nodes) {
    const nodeKey = node.id || node.object?.uuid;
    const matches = uuid
      ? node.object?.uuid === uuid || node.id === uuid
      : !!(
          name &&
          (node.object?.name === name ||
            node.name === name ||
            node.object?.name === name)
        );

    if (matches) {
      return nodeKey
        ? [getGlbHierarchyExpandKey(glbRootId, nodeKey)]
        : [];
    }

    if (node.children?.length) {
      const childPath = collectGlbHierarchyExpandKeys(node.children, {
        uuid,
        name,
        glbRootId,
      });
      if (childPath) {
        return nodeKey
          ? [getGlbHierarchyExpandKey(glbRootId, nodeKey), ...childPath]
          : childPath;
      }
    }
  }

  return null;
}

export const toggleGLBExpansion = (glbId, expandedGLBs, setExpandedGLBs) => {
  // Validate inputs
  if (typeof glbId !== 'string' || !glbId) {
    console.warn('Invalid glbId: must be a non-empty string');
    return;
  }
  if (!(expandedGLBs instanceof Set)) {
    console.warn('Invalid expandedGLBs: must be a Set');
    return;
  }
  if (typeof setExpandedGLBs !== 'function') {
    console.warn('Invalid setExpandedGLBs: must be a function');
    return;
  }

  const newExpanded = new Set(expandedGLBs);
  if (newExpanded.has(glbId)) {
    newExpanded.delete(glbId);
  } else {
    newExpanded.add(glbId);
  }
  setExpandedGLBs(newExpanded);
};

export const deleteGLBMesh = (
  mesh,
  scene,
  glbMeshes,
  setGLBMeshes,
  setSelectedGLBMeshPop
) => {
  // Validate inputs
  if (
    !mesh ||
    !mesh.id ||
    !mesh.parentGLB ||
    (!mesh.parentGLB._id && !mesh.parentGLB.name)
  ) {
    console.warn('Invalid mesh: missing id or parentGLB with _id/name');
    return;
  }
  if (!scene || typeof scene.getObjectByName !== 'function') {
    console.warn('Invalid scene: must be a valid Three.js Scene');
    return;
  }
  if (!glbMeshes || typeof glbMeshes !== 'object') {
    console.warn('Invalid glbMeshes: must be an object');
    return;
  }
  if (typeof setGLBMeshes !== 'function') {
    console.warn('Invalid setGLBMeshes: must be a function');
    return;
  }

  const meshObject = scene.getObjectByName(mesh.id) || mesh.object;
  if (meshObject && meshObject.parent) {
    meshObject.parent.remove(meshObject);
    const updatedGLBMeshes = { ...glbMeshes };
    const parentGLBId = mesh.parentGLB._id || mesh.parentGLB.name;
    if (updatedGLBMeshes[parentGLBId]) {
      updatedGLBMeshes[parentGLBId] = updatedGLBMeshes[parentGLBId].filter(
        (m) => m.id !== mesh.id
      );
    }
    setGLBMeshes(updatedGLBMeshes);
  } else {
    console.warn(`Mesh not found in scene or has no parent: ${mesh.id}`);
  }

  if (typeof setSelectedGLBMeshPop === 'function') {
    setSelectedGLBMeshPop(null);
  }
};
